import React, { useState } from 'react';
import Reveal from './Reveal';
import Phone from './Phone';
import captureWebp from '../assets/screens/capture.webp';
import capturePng from '../assets/screens/capture.png';
import reviewWebp from '../assets/screens/review.webp';
import reviewPng from '../assets/screens/review.png';
import personalizedWebp from '../assets/screens/personalized.webp';
import personalizedPng from '../assets/screens/personalized.png';
import allergenWebp from '../assets/screens/allergen.webp';
import allergenPng from '../assets/screens/allergen.png';
import recipeWebp from '../assets/screens/recipe.webp';
import recipePng from '../assets/screens/recipe.png';
import devicecookWebp from '../assets/screens/devicecook.webp';
import devicecookPng from '../assets/screens/devicecook.png';
import cookWebp from '../assets/screens/cook.webp';
import cookPng from '../assets/screens/cook.png';
import lockscreenWebp from '../assets/screens/lockscreen.webp';
import lockscreenPng from '../assets/screens/lockscreen.png';
import shareWebp from '../assets/screens/share.webp';
import sharePng from '../assets/screens/share.png';
import importWebp from '../assets/screens/import.webp';
import importPng from '../assets/screens/import.png';
import link2Webp from '../assets/screens/link2.webp';
import link2Png from '../assets/screens/link2.png';
import link3Webp from '../assets/screens/link3.webp';
import link3Png from '../assets/screens/link3.png';
import kitchenWebp from '../assets/screens/kitchen.webp';
import kitchenPng from '../assets/screens/kitchen.png';
import localWebp from '../assets/screens/local.webp';
import localPng from '../assets/screens/local.png';
import quickactionsWebp from '../assets/screens/quickactions.webp';
import quickactionsPng from '../assets/screens/quickactions.png';
import collectionplanWebp from '../assets/screens/collectionplan.webp';
import collectionplanPng from '../assets/screens/collectionplan.png';
import collectionpersonWebp from '../assets/screens/collectionperson.webp';
import collectionpersonPng from '../assets/screens/collectionperson.png';
import intakeWebp from '../assets/screens/intake.webp';
import intakePng from '../assets/screens/intake.png';

/**
 * Screens strip (handoff §5): phones in a snap-scrolling row, each with a
 * caption. The screens are images inside the CSS bezel, lazy-loaded (only
 * the hero phone is eager). The cook's shots are one staged salmon
 * avocado bowl, captured 2026-10-04 from the 1.7 build
 * (.maestro/staging/: marketing-stage, shot-generate-photo, shot-scan,
 * marketing-shots, shot-extras, shot-collections); Kitchen and the dietary
 * notice were recaptured 2026-10-06 from the 1.8 build. The order
 * follows the cook: capture it, review it, cook it (from the Lock Screen),
 * share it, and the kitchen and the day around it.
 */
const screens = [
  {
    webp: captureWebp,
    png: capturePng,
    alt: 'The Snapcipe Scan a Dish screen holding a photo of a salmon, avocado, quinoa and cucumber bowl, with a Retake button, a note that this uses 1 AI action and gives a recipe to review before saving, and an Analyze photo button.',
    featured: true,
    title: 'Capture',
    caption:
      'Take the photo, or pick one you already have. One AI action, and you review before anything is saved.',
  },
  {
    webp: reviewWebp,
    png: reviewPng,
    alt: 'The Snapcipe analysis screen with the result for the photographed bowl: Salmon Quinoa Bowl with Avocado, Cucumber, and Sesame Seeds, Modern American, 4 servings, 30 minutes, a description, prep and cook times of 15 minutes each, and nine ingredients, with a View Recipe button.',
    featured: true,
    title: 'Recipe out',
    caption: 'Name, times, ingredients, method - written while you watch.',
  },
  {
    webp: personalizedWebp,
    png: personalizedPng,
    alt: 'Review before saving, with a vegan preference set: the scanned salmon bowl, Serves 4, a note that reads Contains salmon, which is not vegan, and the nine ingredients, each removable, above Save recipe and a Not quite? Fix it action.',
    title: 'Fit to how you eat',
    caption:
      'Same dish, vegan profile: the scan stays honest about what was on the plate and says what clashes. The rewrite - “Make this fit how I eat” - is one tap, and yours to ask for.',
  },
  {
    webp: allergenWebp,
    png: allergenPng,
    alt: 'A recipe page for a seared salmon, quinoa, cucumber and avocado bowl with a Vegan diet set: a notice above the ingredients reads Contains salmon fillet, skin removed, honey - that does not fit how you eat (Vegan).',
    title: 'Checked against how you eat',
    caption:
      'Allergies and diet are checked against every ingredient, and said once, in plain words.',
  },
  {
    webp: recipeWebp,
    png: recipePng,
    alt: 'A recipe in Snapcipe: a photo of the salmon quinoa bowl, the note that it contains salmon and is not vegan, 30 minutes, 4 servings and 620 kcal per serving, the ingredient list with add-to-shopping buttons, and Start cooking.',
    featured: true,
    title: 'The recipe',
    caption: 'Scaled to your servings. One tap sends what you lack to the list.',
  },
  {
    webp: devicecookWebp,
    png: devicecookPng,
    alt: 'The guided device cook sheet in Snapcipe: the salmon avocado bowl re-sequenced for a Thermomix-style cooker - the oven steps listed as by hand, the quinoa step with 15 min, 100 °C, speed 2 and the simmering basket - and Copy and Share steps buttons.',
    title: 'For your kitchen machine',
    caption: 'One tap turns the method into device steps - times, temperatures, speeds.',
  },
  {
    webp: cookWebp,
    png: cookPng,
    alt: 'The Snapcipe cook mode screen: step 4 of 7 over the dish photo - Roast the salmon for 12 minutes until just firm - with a running timer chip at 11:45 and the next step previewed below.',
    featured: true,
    title: 'Cook mode',
    caption: 'One step per screen. Every duration is a timer.',
  },
  {
    webp: lockscreenWebp,
    png: lockscreenPng,
    alt: 'An iPhone Lock Screen showing the Snapcipe Live Activity: Salmon avocado bowl, an 11-minute countdown, step 4 of 7, the step text, and Stop timer and Next step buttons.',
    featured: true,
    title: 'Lock Screen',
    caption: 'The step and the timer, without unlocking.',
  },
  {
    webp: shareWebp,
    png: sharePng,
    alt: 'The Snapcipe share sheet: a 9:16 Stories card of the salmon bowl with the Snapcipe chip and the recipe name, a 9:16 or 1:1 format toggle, a Just the photo switch, a Share the photo card button and Send the recipe.',
    featured: true,
    title: 'Share it',
    caption: 'A card for Stories, Reels or a group chat - with or without the recipe.',
  },
  {
    webp: importWebp,
    png: importPng,
    alt: 'The Snapcipe From a link screen with a TikTok link pasted in: "TikTok link detected - we read the video caption", an Import button, and a row of supported recipe sites.',
    title: 'From a link · 1',
    caption: 'Paste it - TikTok, Instagram, Facebook, YouTube, or a recipe site.',
  },
  {
    webp: link2Webp,
    png: link2Png,
    alt: 'The Snapcipe analysis screen reading a BBC Good Food lasagne page: the recipe name, Italian, 4 servings, 75 minutes, a description, prep and cook times, and the ingredients arriving.',
    title: 'From a link · 2',
    caption: 'We read the page like your browser would, and write the recipe while you watch.',
  },
  {
    webp: link3Webp,
    png: link3Png,
    alt: 'Review before saving: Easy Classic Lasagne, Serves 4, a warning that it may contain dairy - parmesan, mozzarella, crème - and the fifteen ingredients, each editable.',
    title: 'From a link · 3',
    caption: 'Reviewed before it lands - allergies checked, every line yours to edit.',
  },
  {
    webp: collectionplanWebp,
    png: collectionplanPng,
    alt: 'A collection in Snapcipe called Super healthy, a way of eating: Breakfast, Lunch and Dinner sections with saved recipes, suggestions from the library with their times, and a Three new ideas action that uses 1 AI action.',
    featured: true,
    title: 'A way of eating',
    caption:
      'A plan with a breakfast, a lunch and a dinner - filled from the library, or with three new ideas.',
  },
  {
    webp: collectionpersonWebp,
    png: collectionpersonPng,
    alt: 'The New collection screen with Someone I cook for chosen: the name Anna, Peanuts ticked under What they cannot eat, the ways of eating below, and a note that these food rules stay on this phone and are not sent anywhere.',
    title: 'Someone you cook for',
    caption:
      'Their favourites, checked against what they cannot eat. Their rules stay on your phone.',
  },
  {
    webp: kitchenWebp,
    png: kitchenPng,
    alt: 'The Snapcipe Kitchen screen: a shopping list grouped by aisle - Meat & seafood, Other, Produce, Pantry & dry goods - with salmon fillets, quinoa, ripe avocado and sesame seeds, each tagged by recipe.',
    featured: true,
    title: 'Kitchen',
    caption: "Grouped by aisle, because that's how you shop.",
  },
  {
    webp: intakeWebp,
    png: intakePng,
    alt: 'The Snapcipe Intake screen: a ring with 580 kcal left, protein, carbs and fat bars against targets, three logged meals, and the week below.',
    title: 'Intake',
    caption: 'Photograph what you ate. The ring and the week do the rest.',
  },
  {
    webp: localWebp,
    png: localPng,
    alt: 'The Snapcipe Eat local screen for Zürich: three dishes worth ordering - Zürcher Geschnetzeltes with why it is from here, what to look for, a dairy flag, and a Cook it at home button - and Rösti below.',
    title: 'Eat local',
    caption: "Where you are, what's worth ordering, and why - flagged against your allergies.",
  },
  {
    webp: quickactionsWebp,
    png: quickactionsPng,
    alt: 'The iPhone home screen with the Snapcipe icon long-pressed: quick actions for Scan a dish, Log a meal, Import from a link and Shopping list.',
    title: 'From the icon',
    caption: 'Long-press for the four doors. Siri and Shortcuts open the same ones.',
  },
];

/*
 * Eighteen phones in a row is a long scroll before Pricing (owner, 5 Oct:
 * "a bit overwhelming"). Eight tell the story - capture, the recipe, cooking
 * it, a plan, the list, sharing; the other ten join the strip, in their
 * place in the order, when asked for.
 */
const headline = screens.filter((screen) => screen.featured);

const Screens = () => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? screens : headline;
  return (
    <section id="screens" className="screens-section" aria-labelledby="screens-headline">
      <div className="screens-head">
        <Reveal>
          <div className="eyebrow">Screens</div>
          <h2 id="screens-headline" className="h2 section-headline" style={{ maxWidth: '20ch' }}>
            Built like the rest of your phone.
          </h2>
          <p className="lead">
            Native materials, real type sizes, tabular numerals. No gradients invented for the sake
            of it.
          </p>
        </Reveal>
      </div>
      <div className="screens-strip" id="screens-strip">
        {shown.map((screen) => (
          <figure className="screen-figure" key={screen.title}>
            <Phone webp={screen.webp} png={screen.png} alt={screen.alt} />
            <figcaption className="screen-caption">
              <div className="h3">{screen.title}</div>
              <p className="body-copy">{screen.caption}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="screens-more">
        <button
          type="button"
          className="cta-secondary"
          aria-expanded={showAll}
          aria-controls="screens-strip"
          onClick={() => setShowAll((value) => !value)}
        >
          {showAll ? `Show ${headline.length} screens` : `Show all ${screens.length} screens`}
        </button>
      </div>
    </section>
  );
};

export default Screens;
